import { useCustomQuery } from '@gripp/shared-logic';
import { DataGrid, LoadingIndicator } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

type Props = {
  workspaceId: string;
  fileId: string;
  maxHeight: number;
  showCheckboxColumn: boolean;
};

const GET_FILE_ASSETS = `
  query GetFileAssets($filter: AssetFilter, $page: PageRequest, $orderBy: OrderByInput) {
    findAssets(filter: $filter, page: $page, orderBy: $orderBy) {
      count
      limit
      offset
      items {
        id
        name
        description
        lastScannedAt
        lastAddress
      }
    }
  }
`;

export const EditFileAssetsList = ({
  workspaceId,
  fileId,
  maxHeight,
  showCheckboxColumn,
}: Props) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<any>();

  // Filter assets to only show those linked to this specific file
  const filter = {
    media: { eq: fileId },
    workspaces: { eq: workspaceId },
  };

  const { data: assetsData, isLoading } = useCustomQuery({
    query: GET_FILE_ASSETS,
    variables: { 
      filter,
      page: { limit: 100, offset: 0 },
      orderBy: { field: 'name', order: 'ASC' }
    },
    cacheKey: ['fileAssets', fileId, workspaceId],
  });

  const columns = useMemo<ColumnDef<any, any>[]>(() => [
    columnHelper.accessor('name', {
      cell: (info) => <Text style={styles.cellText}>{info.getValue()}</Text>,
      header: () => t('file.assets.name'),
      meta: { sortable: true },
    }),
    columnHelper.accessor('description', {
      cell: (info) => <Text style={styles.cellText}>{info.getValue() || '-'}</Text>,
      header: () => t('file.assets.description'),
      meta: { sortable: true },
    }),
    columnHelper.accessor('lastScannedAt', {
      cell: (info) => {
        const date = info.getValue();
        return date ? <Text>{new Date(date).toLocaleDateString()}</Text> : <Text>-</Text>;
      },
      header: () => t('file.assets.lastScanned'),
      meta: { sortable: true },
    }),
    columnHelper.accessor('lastAddress', {
      cell: (info) => {
        const value = info.getValue();
        return <Text style={styles.cellText}>{value || '-'}</Text>;
      },
      header: () => t('file.assets.lastAddress'),
      meta: { sortable: true },
    }),
  ], [t]);

  const assets = assetsData?.findAssets?.items || [];

  if (isLoading) {
    return (
      <View style={[styles.container, { maxHeight }]}>
        <LoadingIndicator />
      </View>
    );
  }

  if (assets.length === 0) {
    return (
      <View style={[styles.container, { maxHeight }]}>
        <Text style={styles.emptyText}>{t('file.assets.noAssets')}</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, { maxHeight }]}>
      <DataGrid
        columns={columns}
        data={assets}
        isLoading={isLoading}
        hideHeader={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 20,
  },
  cellText: {
    fontSize: 14,
    lineHeight: 20,
  },
}); 